import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FullscreenFormDialog } from "@/components/ui/fullscreen-form-dialog";
import { DragDropZone } from "@/components/ui/drag-drop-zone";
import { TagInput } from "@/components/ui/tag-input";

import { toast } from "@/hooks/useToast";
import { captureException } from "@/lib/sentry";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useProjects } from "@/hooks/useProjects";
import { useOrgNavigate } from "@/hooks/useOrgNavigate";
import { useTranslation } from "@/hooks/useTranslation";
import { uploadVaultFile } from "@/hooks/useUploadVaultFiles";
import { VAULT_ACCEPTED_TYPES, VAULT_FILE_TYPES, guessVaultFileType } from "@/components/vault/vaultFileTypes";

const createFileSchema = (t: (key: string) => string) => z.object({
  name: z.string().optional(),
  project_id: z.string().optional(),
  file_type: z.string().min(1, t('fileUpload.validation.fileTypeRequired')),
  tags: z.array(z.string()).default([]),
});

type FileFormData = z.infer<ReturnType<typeof createFileSchema>>;

const commonTagSuggestions = [
  'financial', 'sustainability', 'report', 'invoice', 'receipt',
  'contract', 'compliance', 'audit', 'budget', 'planning',
  'research', 'presentation', 'meeting-notes', 'policy',
  'training', 'certification', 'legal', 'marketing'
];

interface UploadFileDialogProps {
  /** Called with `false` to request close — the caller unmounts this component. */
  onOpenChange: (open: boolean) => void;
  /** Optional extra callback after a successful upload (e.g. list invalidation). */
  onUploaded?: () => void;
  /** Navigate to the Vault on success. Default true (e.g. quick-add); the Vault
   *  page opts out so the user stays on the grid. */
  navigateOnSuccess?: boolean;
}

/**
 * The fullscreen "Upload" document form (document-metaphor per
 * docs/FULLSCREEN_FORM_MODALS.md). Controlled by the caller and mounted only
 * while open. Used by the quick-add panel; the Vault page itself uploads
 * inline through `VaultUploadCard`.
 */
export function UploadFileDialog({ onOpenChange, onUploaded, navigateOnSuccess = true }: UploadFileDialogProps) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const { user } = useAuth();
  const { selectedOrganization } = useOrganization();
  const { data: projects } = useProjects(selectedOrganization?.id);
  const orgNavigate = useOrgNavigate();
  const queryClient = useQueryClient();

  const fileSchema = useMemo(() => createFileSchema(t), [t]);

  const form = useForm<FileFormData>({
    resolver: zodResolver(fileSchema),
    defaultValues: { name: '', project_id: '', file_type: '', tags: [] },
  });
  const { handleSubmit, setValue, watch } = form;

  const onSubmit = async (data: FileFormData) => {
    if (selectedFiles.length === 0 || !user || !selectedOrganization) return;

    try {
      setUploading(true);
      const organizationId = selectedOrganization.id;

      // Upload files sequentially (bytes, then the row through Hono — the
      // shared path the inline Vault upload card uses too).
      for (const file of selectedFiles) {
        // For a single file with a custom name, keep the original extension.
        const extension = file.name.includes('.') ? '.' + file.name.split('.').pop() : '';
        let finalName: string;
        if (selectedFiles.length === 1 && data.name?.trim()) {
          const customName = data.name.trim();
          finalName = customName.endsWith(extension) ? customName : customName + extension;
        } else {
          finalName = file.name;
        }

        await uploadVaultFile(organizationId, {
          file,
          name: finalName,
          file_type: data.file_type,
          project_id: data.project_id || null,
          tags: data.tags || [],
        });
      }

      toast.success(t('fileUpload.uploadSuccess', { count: selectedFiles.length }));

      // Refresh the vault list, continue to the Vault, then close.
      // Navigate with `replace` BEFORE closing so the wrapper skips its own
      // history.back() (docs/FULLSCREEN_FORM_MODALS.md "create, then continue").
      queryClient.invalidateQueries({ queryKey: ['files'] });
      onUploaded?.();
      // Continue to the Vault when opened from outside it (e.g. quick-add).
      // The Vault page itself opts out (navigateOnSuccess=false) so the user
      // stays put on the grid they just uploaded into.
      if (navigateOnSuccess) {
        orgNavigate('/resources/vault', { replace: true });
      }
      onOpenChange(false);
    } catch (error) {
      toast.errorTemplates.generic();
      captureException(error as Error, {
        context: 'Vault file upload',
        fileCount: selectedFiles.length,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles(files);

    if (files.length === 1) {
      const file = files[0];
      setValue('name', file.name.split('.').slice(0, -1).join('.'));

      // Auto-detect file type based on extension
      setValue('file_type', guessVaultFileType(file.name));
    } else if (files.length > 1) {
      setValue('name', `${files.length} files selected`);
      setValue('file_type', 'document'); // Default for multiple files
    }
  };

  return (
    <FullscreenFormDialog
      open
      onOpenChange={onOpenChange}
      title={t('fileUpload.title')}
      description={t('fileUpload.description')}
      onSubmit={handleSubmit(onSubmit)}
      formId="upload-file-form"
      submitLabel={t('fileUpload.submitLabel', { count: selectedFiles.length })}
      submittingLabel={t('fileUpload.submitting')}
      isSubmitting={uploading}
      submitDisabled={selectedFiles.length === 0}
      skipCloseConfirmation={!form.formState.isDirty && selectedFiles.length === 0}
    >
      <Form {...form}>
        <div className="space-y-8">
          {/* Links row — internal associations above the document */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="project_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fileUpload.fields.project')}</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}
                    value={field.value || 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('fileUpload.placeholders.project')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">{t('fileUpload.noProject')}</SelectItem>
                      {projects?.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          {/* Document card */}
          <Card>
            <CardContent className="pt-6 space-y-8">
              <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">
                {t('fileUpload.heroTitle')}
              </h2>

              {/* Key zone: the drop area */}
              <div className="-mx-6 px-6 py-6 bg-muted space-y-5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t('fileUpload.sections.files')}</h3>
                <DragDropZone
                  onFilesSelected={handleFilesSelected}
                  accept={VAULT_ACCEPTED_TYPES.join(",")}
                  multiple={true}
                  className="min-h-[120px] bg-background"
                >
                  {selectedFiles.length > 0 && (
                    <div className="p-4">
                      <div className="text-center mb-4">
                        <h3 className="font-semibold">
                          {t('fileUpload.filesSelected', { count: selectedFiles.length })}
                        </h3>
                      </div>
                      <div className="space-y-1 max-h-24 overflow-y-auto">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="text-xs text-muted-foreground flex items-center justify-between bg-background rounded px-2 py-1">
                            <span className="truncate">{file.name}</span>
                            <span>{(file.size / 1024 / 1024).toFixed(1)}MB</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </DragDropZone>
              </div>

              {/* Details */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t('fileUpload.sections.details')}</h3>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-extralight">{t('fileUpload.fields.fileName')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('fileUpload.placeholders.fileName')} {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="file_type"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel className="font-extralight">{t('fileUpload.fields.fileType')}</FormLabel>
                          {fieldState.error && (
                            <span className="text-xs text-destructive">{fieldState.error.message}</span>
                          )}
                        </div>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className={fieldState.error ? "border-destructive" : ""}>
                              <SelectValue placeholder={t('fileUpload.placeholders.fileType')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {VAULT_FILE_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>{t(`fileUpload.fileTypes.${type}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <div>
                    <Label>{t('fileUpload.fields.tags')}</Label>
                    <TagInput
                      tags={watch('tags') || []}
                      onTagsChange={(tags) => setValue('tags', tags, { shouldDirty: true })}
                      suggestions={commonTagSuggestions}
                      placeholder={t('fileUpload.placeholders.tags')}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </Form>
    </FullscreenFormDialog>
  );
}
